import React, { useState, useEffect, useMemo } from 'react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import CostCalculatorHero from '../components/CostCalculatorHero';
import CostCalculatorInputs from '../components/CostCalculatorInputs';
import AlertaioPromo from '../components/AlertaioPromo';
import { useSEO } from '../utils/seo';

interface LLMCost {
  provider: string;
  model: string;
  operator: string;
  input_cost_per_1m: number;
  output_cost_per_1m: number;
  prompt_cache_write_per_1m?: number;
  prompt_cache_read_per_1m?: number;
  show_in_playground?: boolean;
  per_call?: number;
  input_audio_per_1m?: number;
  output_audio_per_1m?: number;
  completion_audio_per_1m?: number;
}

interface LLMCostData {
  metadata: {
    total_models: number;
    note: string;
    operators_explained: {
      equals: string;
      startsWith: string;
      includes: string;
    };
  };
  data: LLMCost[];
}

const CostCalculator: React.FC = () => {
  useSEO({
    title: 'AI API Cost Calculator - Compare 1000+ LLM Models Pricing | Alertaio',
    description: 'Free AI API cost calculator to compare pricing across OpenAI GPT-4, Claude, Gemini, Llama, and 1000+ AI models. Calculate costs for input/output tokens and API calls in real-time.',
    keywords: 'AI cost calculator, LLM pricing comparison, OpenAI pricing, GPT-4 cost, Claude pricing, AI API cost, token calculator, model pricing, AI budget calculator',
    canonicalUrl: 'https://alertaio.com/cost-calculator',
    ogImage: 'https://alertaio.com/og-cost-calculator.png',
    type: 'website'
  });

  useEffect(() => {
    const schema = {
      '@context': 'https://schema.org',
      '@type': 'WebApplication',
      name: 'AI API Cost Calculator',
      description: 'Compare and calculate costs across 1000+ AI models from OpenAI, Anthropic, Google, AWS, Azure, and more',
      applicationCategory: 'DeveloperApplication',
      offers: {
        '@type': 'Offer',
        price: '0',
        priceCurrency: 'USD'
      },
      featureList: 'Real-time pricing data, 1000+ AI models, Cost comparison, Token calculator, Multi-provider support'
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(schema);
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  const [costData, setCostData] = useState<LLMCostData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Filter states
  const [selectedProvider, setSelectedProvider] = useState<string>('ALL');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState<'model' | 'input' | 'output' | 'total'>('model');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  
  // Calculator states
  const [inputTokens, setInputTokens] = useState<number>(1000000);
  const [outputTokens, setOutputTokens] = useState<number>(1000000);
  const [numberOfCalls, setNumberOfCalls] = useState<number>(1);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 50;

  useEffect(() => {
    fetchCostData();
  }, []);

  const fetchCostData = async () => {
    try {
      setLoading(true);
      const response = await fetch('https://www.helicone.ai/api/llm-costs');
      if (!response.ok) throw new Error('Failed to fetch cost data');
      const data: LLMCostData = await response.json();
      setCostData(data);
      setError(null);
    } catch (err) {
      setError('Failed to load cost data. Please try again later.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const providers = useMemo(() => {
    if (!costData) return [];
    const uniqueProviders = Array.from(new Set(costData.data.map(item => item.provider)));
    
    // Define priority order for popular providers
    const priorityProviders = [
      'OPENAI',
      'ANTHROPIC',
      'DEEPSEEK',
      'GOOGLE',
      'AWS',
      'AZURE',
      'MISTRAL',
      'META',
      'COHERE',
      'GROQ'
    ];
    
    // Custom sort: priority providers first, then alphabetically
    return uniqueProviders.sort((a, b) => {
      const aIndex = priorityProviders.indexOf(a.toUpperCase());
      const bIndex = priorityProviders.indexOf(b.toUpperCase());
      
      // Both are priority providers
      if (aIndex !== -1 && bIndex !== -1) {
        return aIndex - bIndex;
      }
      
      // Only a is priority
      if (aIndex !== -1) return -1;
      
      // Only b is priority
      if (bIndex !== -1) return 1;
      
      // Neither is priority, sort alphabetically
      return a.localeCompare(b);
    });
  }, [costData]);

  const calculateCost = (model: LLMCost): number => {
    const inputCost = (inputTokens / 1000000) * model.input_cost_per_1m;
    const outputCost = (outputTokens / 1000000) * model.output_cost_per_1m;
    const totalPerCall = inputCost + outputCost;
    return totalPerCall * numberOfCalls;
  };

  const filteredAndSortedData = useMemo(() => {
    if (!costData) return [];
    
    let filtered = costData.data;
    
    // Define priority order for popular providers
    const priorityProviders = [
      'OPENAI',
      'ANTHROPIC',
      'DEEPSEEK',
      'GOOGLE',
      'AWS',
      'AZURE',
      'MISTRAL',
      'META',
      'COHERE',
      'GROQ'
    ];
    
    const getProviderPriority = (provider: string): number => {
      const index = priorityProviders.indexOf(provider.toUpperCase());
      return index !== -1 ? index : 999; // Non-priority providers get high number
    };
    
    // Apply provider filter
    if (selectedProvider !== 'ALL') {
      filtered = filtered.filter(item => item.provider === selectedProvider);
    }
    
    // Apply search filter
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(item => 
        item.model.toLowerCase().includes(search) ||
        item.provider.toLowerCase().includes(search)
      );
    }
    
    // Apply sorting
    const sorted = [...filtered].sort((a, b) => {
      let aValue: number | string;
      let bValue: number | string;
      
      switch (sortBy) {
        case 'model':
          // First sort by provider priority, then by model name
          const aPriority = getProviderPriority(a.provider);
          const bPriority = getProviderPriority(b.provider);
          
          if (aPriority !== bPriority) {
            return sortOrder === 'asc' 
              ? aPriority - bPriority
              : bPriority - aPriority;
          }
          
          // Same provider priority, sort by model name
          aValue = a.model.toLowerCase();
          bValue = b.model.toLowerCase();
          break;
        case 'input':
          aValue = a.input_cost_per_1m;
          bValue = b.input_cost_per_1m;
          break;
        case 'output':
          aValue = a.output_cost_per_1m;
          bValue = b.output_cost_per_1m;
          break;
        case 'total':
          aValue = calculateCost(a);
          bValue = calculateCost(b);
          break;
        default:
          return 0;
      }
      
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return sortOrder === 'asc' 
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }
      
      return sortOrder === 'asc' 
        ? (aValue as number) - (bValue as number)
        : (bValue as number) - (aValue as number);
    });
    
    return sorted;
  }, [costData, selectedProvider, searchTerm, sortBy, sortOrder, inputTokens, outputTokens, numberOfCalls]);

  // Pagination
  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredAndSortedData.slice(startIndex, endIndex);
  }, [filteredAndSortedData, currentPage]);

  const totalPages = Math.ceil(filteredAndSortedData.length / itemsPerPage);

  const handleSort = (column: 'model' | 'input' | 'output' | 'total') => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
    setCurrentPage(1);
  };

  const SortIcon = ({ column }: { column: 'model' | 'input' | 'output' | 'total' }) => {
    if (sortBy !== column) return <span className="text-gray-400">⇅</span>;
    return sortOrder === 'asc' ? <span>↑</span> : <span>↓</span>;
  };

  const Pagination = () => {
    const pageNumbers = [];
    const maxPagesToShow = 5;
    
    let startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
    let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);
    
    if (endPage - startPage < maxPagesToShow - 1) {
      startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }
    
    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }
    
    return (
      <div className="flex items-center justify-center gap-2 mt-6">
        <button
          onClick={() => setCurrentPage(1)}
          disabled={currentPage === 1}
          className="px-3 py-2 rounded-lg border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          First
        </button>
        <button
          onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
          disabled={currentPage === 1}
          className="px-3 py-2 rounded-lg border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Previous
        </button>
        
        {pageNumbers.map(number => (
          <button
            key={number}
            onClick={() => setCurrentPage(number)}
            className={`px-3 py-2 rounded-lg border text-sm font-medium ${
              currentPage === number
                ? 'bg-indigo-600 text-white border-indigo-600'
                : 'border-gray-300 text-gray-700 hover:bg-gray-50'
            }`}
          >
            {number}
          </button>
        ))}
        
        <button
          onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
          disabled={currentPage === totalPages}
          className="px-3 py-2 rounded-lg border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Next
        </button>
        <button
          onClick={() => setCurrentPage(totalPages)}
          disabled={currentPage === totalPages}
          className="px-3 py-2 rounded-lg border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Last
        </button>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* SEO Hero Section */}
        <CostCalculatorHero />

        {/* Calculator Inputs */}
        <CostCalculatorInputs
          inputTokens={inputTokens}
          setInputTokens={setInputTokens}
          outputTokens={outputTokens}
          setOutputTokens={setOutputTokens}
          numberOfCalls={numberOfCalls}
          setNumberOfCalls={setNumberOfCalls}
        />

        {/* Alertaio Promotion */}
        <AlertaioPromo />

        {/* Main Calculator Table */}
        <div className="bg-white rounded-lg shadow-lg p-8">
          <div className="flex justify-between items-center mb-4">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">
                Compare OpenAI Model Costs
              </h2>
              <p className="text-sm text-indigo-600 mt-1">
                Support for Anthropic, Google Gemini, and other providers coming soon!
              </p>
            </div>
            <p className="text-sm text-gray-600">
              {filteredAndSortedData.length} models found
            </p>
          </div>

          {/* Filters */}
          <div className="grid md:grid-cols-2 gap-4 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Filter by Provider
              </label>
              <select
                value={selectedProvider}
                onChange={(e) => {
                  setSelectedProvider(e.target.value);
                  setCurrentPage(1);
                }}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              >
                <option value="ALL">All Providers</option>
                {providers.map(provider => (
                  <option key={provider} value={provider}>{provider}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Search Models
              </label>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
                placeholder="Search by model or provider..."
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
            </div>
          </div>

          {loading && (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-indigo-600 border-t-transparent"></div>
              <p className="mt-4 text-gray-600">Loading AI model pricing data...</p>
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
              {error}
            </div>
          )}

          {!loading && !error && costData && (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th
                        onClick={() => handleSort('model')}
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      >
                        <div className="flex items-center gap-2">
                          Model <SortIcon column="model" />
                        </div>
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Provider
                      </th>
                      <th
                        onClick={() => handleSort('input')}
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      >
                        <div className="flex items-center gap-2">
                          Input (per 1M) <SortIcon column="input" />
                        </div>
                      </th>
                      <th
                        onClick={() => handleSort('output')}
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      >
                        <div className="flex items-center gap-2">
                          Output (per 1M) <SortIcon column="output" />
                        </div>
                      </th>
                      <th
                        onClick={() => handleSort('total')}
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      >
                        <div className="flex items-center gap-2">
                          Total Cost <SortIcon column="total" />
                        </div>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {paginatedData.map((model, index) => {
                      const totalCost = calculateCost(model);
                      const inputCost = (inputTokens / 1000000) * model.input_cost_per_1m;
                      const outputCost = (outputTokens / 1000000) * model.output_cost_per_1m;
                      const perCallCost = inputCost + outputCost;
                      
                      return (
                        <tr key={index} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {model.model}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                              {model.provider}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                            ${model.input_cost_per_1m.toFixed(2)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                            ${model.output_cost_per_1m.toFixed(2)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm">
                              <div className="font-semibold text-gray-900">
                                ${totalCost.toFixed(2)}
                              </div>
                              <div className="text-xs text-gray-500">
                                ${perCallCost.toFixed(2)} per call
                              </div>
                              {model.prompt_cache_write_per_1m && (
                                <div className="text-xs text-blue-600 mt-1">
                                  Cache: ${model.prompt_cache_write_per_1m.toFixed(2)}/1M
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <Pagination />

              <div className="mt-6 text-sm text-gray-600 bg-blue-50 p-4 rounded-lg">
                <p className="font-semibold mb-2">💡 Pro Tip:</p>
                <p>
                  Showing {((currentPage - 1) * itemsPerPage) + 1}-{Math.min(currentPage * itemsPerPage, filteredAndSortedData.length)} of {filteredAndSortedData.length} models. 
                  Costs calculated for {inputTokens.toLocaleString()} input tokens, {outputTokens.toLocaleString()} output tokens, and {numberOfCalls.toLocaleString()} API {numberOfCalls === 1 ? 'call' : 'calls'}.
                  Use <a href="/" className="text-indigo-600 hover:underline font-semibold">Alertaio</a> to monitor your actual spending across all providers.
                </p>
              </div>
            </>
          )}
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default CostCalculator;
